/**
 * useDoctorSelection Hook
 * Manages doctor selection and abbreviation changes
 */

import { useCallback } from 'react';
import { push } from 'react-router-redux';
import { changeDoctorAbbr } from '../../../redux/modules/auth';
import { getDoctorListOptions } from '../../CdpPay/functions';
import { buildCalendarUrl } from '../utils/urlUtils';

/**
 * Custom hook for managing doctor selection
 * @param {Object} user - Current user object
 * @param {Object} match - React Router match object
 * @param {string} currentDate - Current selected date
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} onDoctorChangeCallback - Callback when doctor changes
 * @returns {Object} { selectedAbbr, doctorOptions, handleDoctorChange, changeDoctorAbbrAction }
 */
export const useDoctorSelection = (user, match, currentDate, dispatch, onDoctorChangeCallback) => {

  // Get current doctor abbreviation from route params
  const selectedAbbr = match?.params?.abbr || null;

  // Generate doctor dropdown options
  const doctorOptions = user ? getDoctorListOptions(user, false) : [];

  /**
   * Change doctor abbreviation in Redux
   * @param {string} abbr - Doctor abbreviation
   */
  const changeDoctorAbbrAction = useCallback((abbr) => {
    if (!abbr) return;
    dispatch(changeDoctorAbbr(abbr));
  }, [dispatch]);

  /**
   * Handle doctor selection change
   * @param {Event} e - Event object
   * @param {Object} data - Semantic UI dropdown data
   */
  const handleDoctorChange = useCallback((e, data) => {
    const newAbbr = data.value;

    // Update Redux state
    changeDoctorAbbrAction(newAbbr);

    // Update URL
    const newUrl = buildCalendarUrl(match.url, currentDate, newAbbr || '');
    dispatch(push(newUrl));

    // Trigger data refresh with doctor change flag
    if (onDoctorChangeCallback) {
      onDoctorChangeCallback('doctorChange');
    }
  }, [changeDoctorAbbrAction, match.url, currentDate, dispatch, onDoctorChangeCallback]);

  return {
    selectedAbbr,
    doctorOptions,
    handleDoctorChange,
    changeDoctorAbbrAction
  };
};
